import { PDF_CONTENT_TYPE } from "@/features/drawings/api";
import type {
  DrawingMatchOutcome,
  OcrConfidence,
  OcrStatus,
  ThumbnailStatus,
} from "@/features/drawings/api";
import type { QrStampArea } from "@/features/drawings/panel/types";
import type { Quotation, QuotationStatus } from "@/features/quotations/api";
import { api } from "@/lib/api";
import type { Pagination } from "@/lib/types";

/** 案件（API の ProjectResponse に対応）。 */
export interface Project {
  project_id: number;
  project_number: string;
  /** 案件名。受注時点で決まっていないことがあるため null あり。 */
  project_title: string | null;
  client_id: number | null;
  client_name: string | null;
  /** 直送先。null は直送しない（得意先へ納める）。 */
  ship_to_id: number | null;
  ship_to_name: string | null;
  client_po_number: string | null;
  /**
   * セット品か。**同一のセットを複数セット作る注文**（客先の基幹がセット 1 本
   * ごとに購番を切るため、同じ内容の購番が複数本で届く）。畳んだ明細の数量は
   * 購番をまたいだ合算になる。
   */
  is_set_product: boolean;
  /** セット数。セット品でなければ null。 */
  set_count: number | null;
  /**
   * この案件の明細に付いている購番（重複なし・昇順）。セット品はセット数と同じ
   * 本数が並ぶ。`client_po_number`（1 本だけ）には収まらないため別に返る。
   */
  client_order_numbers: string[];
  sales_rep_id: string | null;
  order_date: string | null;
  deadline: string | null;
  shipping_planned_date: string | null;
  /** 出荷日時。null なら未出荷。 */
  shipped_at: string | null;
  note: string | null;
  item_count: number;
  /** 見積未紐付けの明細行数。特急案件の見積後付け漏れに気づくためのもの。 */
  unlinked_item_count: number;
  /**
   * 起票元の見積。案件は見積から起こすので、どの見積でいくら受けたのかを
   * 辿れるように返る。繋がりは明細の紐付け（`quotation_item_id`）から辿るので、
   * 手で作った案件では null。
   *
   * **詳細（取得・更新）でのみ入る**。一覧では常に null。
   */
  source_quotation_id: number | null;
  source_quotation_number: string | null;
  /**
   * この案件のために起こした（または選んだ）見積。特急フローで入る。
   *
   * **まだ受注になっていなくても入る**ので、作りかけの見積を案件の画面から
   * 開ける。`source_quotation_*`（明細の紐付けから辿る＝確定した見積）とは別。
   * **詳細でのみ入る**。
   */
  raised_quotation_id: number | null;
  raised_quotation_number: string | null;
  raised_quotation_status: QuotationStatus | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProjectListResponse {
  items: Project[];
  pagination: Pagination;
}

export interface ProjectListParams {
  keyword?: string;
  clientId?: number;
  /** 未指定なら出荷済み・未出荷の両方。 */
  shipped?: boolean;
  /**
   * true で「見積と紐付いていない明細を持つ案件」だけに絞る（特急案件の見積
   * 後付け漏れを拾う）。未指定なら絞らない。
   */
  unlinked?: boolean;
  sortKey?: string;
  ascending?: boolean;
  limit: number;
  offset: number;
}

export function listProjects(params: ProjectListParams): Promise<ProjectListResponse> {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.keyword) query.set("keyword", params.keyword);
  if (params.clientId != null) query.set("client_id", String(params.clientId));
  if (params.shipped != null) query.set("shipped", String(params.shipped));
  if (params.unlinked != null) query.set("unlinked", String(params.unlinked));
  if (params.sortKey) query.set("sort_key", params.sortKey);
  if (params.ascending != null) query.set("ascending", String(params.ascending));
  return api.get<ProjectListResponse>(`/projects?${query}`);
}

export function getProject(projectId: number): Promise<Project> {
  return api.get<Project>(`/projects/${projectId}`);
}

/** 案件ヘッダの登録・更新で送れる項目。出荷は専用の API を使う。 */
export type ProjectInput = Partial<
  Pick<
    Project,
    | "project_number"
    | "project_title"
    | "client_id"
    | "ship_to_id"
    | "client_po_number"
    | "set_count"
    | "sales_rep_id"
    | "order_date"
    | "deadline"
    | "shipping_planned_date"
    | "note"
  >
>;

/** 発注 Excel から起こす明細 1 行の下見（API の ProjectImportItemPreview）。 */
export interface ProjectImportItemPreview {
  drawing_number: string;
  item_name: string;
  /** セット品は客先購番をまたいだ合算（1 セットあたり × セット数）。 */
  quantity: number;
  client_unit_no: string | null;
  client_bom_no: string | null;
  note: string | null;
  client_order_numbers: string[];
}

/** 発注 Excel の取り込み結果（下見・実行で同じ形）。 */
export interface ProjectImportResult {
  /** true なら何も保存していない（取り込み前の下見）。 */
  dry_run: boolean;
  client_id: number;
  client_name: string;
  project_title: string | null;
  client_order_numbers: string[];
  is_set_product: boolean;
  set_count: number | null;
  item_count: number;
  /** 畳む前の行数（客先購番 × 部品）。 */
  paper_row_count: number;
  items: ProjectImportItemPreview[];
  /** 取り込みは止めない気づき（読めずに飛ばした行など）。 */
  warnings: string[];
  /** 取り込めない理由。空でなければ実行できない。 */
  blockers: string[];
  can_apply: boolean;
  /** 下見のときは null。 */
  project: Project | null;
}

/**
 * 発注明細の Excel（京セラ）から案件を起こす。
 *
 * `dryRun` で下見と実行を切り替える。どちらも同じ解析を通るので、下見で見えた
 * 内容がそのまま起票される。**人が入れた値は紙より優先**なので、画面で埋めた
 * 項目だけを `input` で渡す。
 */
export function importProjectOrder(params: {
  file: File;
  dryRun: boolean;
  input?: ProjectInput;
}): Promise<ProjectImportResult> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("dry_run", String(params.dryRun));
  for (const [key, value] of Object.entries(params.input ?? {})) {
    if (value != null && value !== "") form.append(key, String(value));
  }
  return api.postForm<ProjectImportResult>("/projects/import", form);
}

/** 新東工業のファイルから起こす明細 1 行の下見（API の ShintoImportItemPreview）。 */
export interface ShintoImportItemPreview {
  /** `作番記号` 側の記号を含む（`MYA A2135`）。空白は 1 つに畳まれている。 */
  drawing_number: string;
  /** 半角カナは全角に直してある。 */
  item_name: string;
  quantity: number;
  /** 紙の行ごとの納期（分納はここで分かれる）。 */
  deadline: string | null;
  /** 紙の単価。**自社の見積ではない**。 */
  client_unit_price: string | null;
  /** 摘要。伝票番号と、内示の行なら印（`内示 / 伝票No: 100276276500`）。 */
  note: string | null;
}

/** 製番 1 つ（= 案件 1 件）ぶんの下見（API の ShintoImportProjectPreview）。 */
export interface ShintoImportProjectPreview {
  /** 製番（`作番記号` + `作番`）。案件名にも入る。 */
  client_po_number: string;
  /** **その製番でいちばん早い注文日**（伝票ごとに注文日が違う紙がある）。 */
  order_date: string | null;
  /**
   * 案件の納期。**行の納期が 1 種類のときだけ入る**。複数あれば null で、納期は
   * 明細ごとに入る。
   */
  deadline: string | null;
  item_count: number;
  items: ShintoImportItemPreview[];
  /**
   * 同じ製番の案件が既にあるときの工番。**入っていればこの製番は取り込まない**。
   */
  existing_project_number: string | null;
}

/**
 * 発注ファイル（新東工業）の取り込み結果（下見・実行で同じ形）。
 *
 * **1 ファイルで複数の案件ができる**ので、京セラの発注 Excel
 * （`ProjectImportResult`）とは形が違う（案件が配列になる）。
 */
export interface ShintoProjectImportResult {
  /** true なら何も保存していない（取り込み前の下見）。 */
  dry_run: boolean;
  client_id: number;
  client_name: string;
  /** 読めた紙の行数（飛ばした行＝納入済・読めない行は含まない）。 */
  row_count: number;
  /** 起こす案件の数（既にある製番は含まない）。 */
  project_count: number;
  /** 製番ごとの内容。**既にある製番も紙の並びで入る**。 */
  projects: ShintoImportProjectPreview[];
  warnings: string[];
  /** 取り込めない理由。空でなければ実行できない。 */
  blockers: string[];
  can_apply: boolean;
  /** 起こした案件。下見のときは空。 */
  created: Project[];
}

/**
 * 案件登録用のファイル（新東工業）から案件を起こす。**製番ごとに 1 件**。
 *
 * **CSV・.xls・.xlsx のどれでも読む**（客先は同じ表を CSV と .xls の両方で送って
 * くる）。様式を決めるのは拡張子ではなくファイルの中身。
 *
 * `dryRun` で下見と実行を切り替える。どちらも同じ解析を通るので、下見で見えた
 * 内容がそのまま起票される。
 *
 * **案件ごとに違う値（案件名・客先注文番号・受注日・納期）は渡せない。** 1 つの
 * 値を全件に入れると客先注文番号が重複するため、CSV の値を使う。案件をまたいで
 * 同じに使える項目だけを渡す。
 */
export function importShintoProjects(params: {
  file: File;
  dryRun: boolean;
  shared?: Pick<ProjectInput, "ship_to_id" | "sales_rep_id" | "note">;
}): Promise<ShintoProjectImportResult> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("dry_run", String(params.dryRun));
  for (const [key, value] of Object.entries(params.shared ?? {})) {
    if (value != null && value !== "") form.append(key, String(value));
  }
  return api.postForm<ShintoProjectImportResult>("/projects/import/shinto", form);
}

/**
 * セット品を切り替える。**明細の形も一緒に変わる**ので案件の更新とは別の口。
 *
 * true で同じ部品の明細を畳んで数量を合算し、false で客先購番ごとの行に分け直す。
 */
export function setProjectSetProduct(
  projectId: number,
  isSetProduct: boolean,
): Promise<Project> {
  return api.post<Project>(`/projects/${projectId}/set-product`, {
    is_set_product: isSetProduct,
  });
}

export function createProject(input: ProjectInput): Promise<Project> {
  return api.post<Project>("/projects", input);
}

/**
 * 案件の明細をコピーして見積を起こす（特急フロー）。
 *
 * 図番を手で打ち直させないための口。**明細の紐付けはまだ張らない**ので、案件も
 * 見積もこの時点では締まらない（docs/urgent-project-flow.md）。
 */
export function raiseQuotationFromProject(projectId: number): Promise<Quotation> {
  return api.post<Quotation>(`/projects/${projectId}/quotation`, {});
}

/** 既にある見積を、この案件のためのものとして選ぶ（客先から後で届いた依頼など）。 */
export function selectQuotationForProject(
  projectId: number,
  quotationId: number,
): Promise<Quotation> {
  return api.put<Quotation>(`/projects/${projectId}/quotation`, {
    quotation_id: quotationId,
  });
}

/** 結び付けを外す。**見積そのものは消さない**（選び間違えたときの戻し道）。 */
export function releaseQuotationOfProject(projectId: number): Promise<Quotation> {
  return api.delete<Quotation>(`/projects/${projectId}/quotation`);
}

/** 案件明細に結び付けられる見積明細の候補（docs/urgent-project-flow.md）。 */
export interface QuotationItemCandidate {
  quotation_item_id: number;
  quotation_id: number;
  quotation_number: string;
  sequence: number;
  drawing_number: string;
  item_name: string;
  quantity: number;
  /** 図番が一致する。並びも一致する行が先。 */
  drawing_number_matches: boolean;
}

/**
 * その案件明細に結び付けられる見積明細を取る。
 *
 * 候補は**まだどの案件明細にも使われていない行**だけ。使われている行を選べると、
 * 選んだ瞬間に別の案件明細から金額の根拠が外れる。
 */
export function listQuotationItemCandidates(
  projectId: number,
  projectItemId: number,
): Promise<{ items: QuotationItemCandidate[] }> {
  return api.get<{ items: QuotationItemCandidate[] }>(
    `/projects/${projectId}/items/${projectItemId}/quotation-item-candidates`,
  );
}

export function updateProject(
  projectId: number,
  input: ProjectInput,
): Promise<Project> {
  return api.patch<Project>(`/projects/${projectId}`, input);
}

export function deleteProject(projectId: number): Promise<void> {
  return api.delete<void>(`/projects/${projectId}`);
}

/**
 * 選んだ案件をまとめて削除する。
 *
 * **1 件でも消せなければ何も消えない**（出荷済みの案件が混ざっていれば 409、
 * 存在しない案件 ID なら 404）。一部だけ消えると、何が残ったのかを数え直す
 * ことになるため。DELETE ではなく POST なのは、消す対象を本文で渡すため。
 */
export function deleteProjects(projectIds: number[]): Promise<void> {
  return api.post<void>("/projects/delete", { project_ids: projectIds });
}

/**
 * 出荷済み / 未出荷を切り替える。
 *
 * 案件まるごとの出荷だけを扱う（案件が持つ出荷日時は 1 つ）。行ごとに違う日で
 * 出す分納は、明細の発送日（`ProjectItem.shipped_date`）で表す。
 */
export function shipProject(projectId: number, shipped: boolean): Promise<Project> {
  return api.post<Project>(`/projects/${projectId}/ship`, { shipped });
}

/** 案件明細（API の ProjectItemResponse に対応）。金額は持たない。 */
export interface ProjectItem {
  project_item_id: number;
  project_id: number;
  sequence: number;
  /** この行に添えられた図面 PDF。まだ無ければ null。 */
  drawing_id: number | null;
  drawing_number: string;
  item_name: string;
  material: string | null;
  surface_treatment: string | null;
  quantity: number;
  /** 客先のユニットNo（京セラ）。客先へ回答を返すときの行の鍵。 */
  client_unit_no: string | null;
  /** 客先の部品表No（京セラ）。同上。 */
  client_bom_no: string | null;
  /** この行に畳み込んだ購番（重複なし・昇順）。 */
  client_order_numbers: string[];
  /**
   * 客先の伝票番号（新東工業）。**行ごとに違う**（客先の基幹では部品 1 点ごとに
   * 伝票が立つ）。請求書の「日付/伝番」欄に刷る。
   */
  client_slip_number: string | null;
  /**
   * 発注書に書かれていた単価。**自社の見積ではない**（あちらは工程別見積の
   * 合算）。発注書を読み取って起こした行にだけ入る。
   */
  client_unit_price: string | null;
  deadline: string | null;
  /**
   * 加工納期（社内で加工を上げる期限）。null なら決めていない。
   *
   * 客先へ約束した `deadline` とは別で、**現場へ出す図面に刷るのはこちら**。
   */
  machining_deadline: string | null;
  /** 明細ごとの出荷予定日。null なら案件の出荷予定日に従う。 */
  shipping_planned_date: string | null;
  /**
   * その行を実際に発送した日。null なら未発送。
   *
   * 行ごとに持つので分納を表せる。案件の `shipped_at`（案件まるごとの出荷）とは
   * 別に動く。
   */
  shipped_date: string | null;
  /** 対応する見積明細。null なら見積未紐付け（特急案件）。 */
  quotation_item_id: number | null;
  note: string | null;
  drawing_revision_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProjectItemListResponse {
  items: ProjectItem[];
}

export function listProjectItems(projectId: number): Promise<ProjectItemListResponse> {
  return api.get<ProjectItemListResponse>(`/projects/${projectId}/items`);
}

/** 明細の追加・更新で送れる項目。案件明細は必ず図面行。 */
export interface ProjectItemInput {
  drawing_number?: string;
  item_name?: string | null;
  material?: string | null;
  surface_treatment?: string | null;
  quantity?: number;
  client_unit_no?: string | null;
  client_bom_no?: string | null;
  client_slip_number?: string | null;
  client_unit_price?: string | null;
  deadline?: string | null;
  machining_deadline?: string | null;
  shipping_planned_date?: string | null;
  shipped_date?: string | null;
  quotation_item_id?: number | null;
  note?: string | null;
}

export function createProjectItem(
  projectId: number,
  input: ProjectItemInput,
): Promise<ProjectItem> {
  return api.post<ProjectItem>(`/projects/${projectId}/items`, input);
}

export function updateProjectItem(
  projectId: number,
  itemId: number,
  input: ProjectItemInput,
): Promise<ProjectItem> {
  return api.patch<ProjectItem>(`/projects/${projectId}/items/${itemId}`, input);
}

export function deleteProjectItem(projectId: number, itemId: number): Promise<void> {
  return api.delete<void>(`/projects/${projectId}/items/${itemId}`);
}

/** 明細の行番号を、渡した並びで 1..N に振り直す（全明細を過不足なく渡す）。 */
export function reorderProjectItems(
  projectId: number,
  itemIds: number[],
): Promise<ProjectItemListResponse> {
  return api.put<ProjectItemListResponse>(`/projects/${projectId}/items/order`, {
    project_item_ids: itemIds,
  });
}

/**
 * 選んだ明細をまとめて削除し、削除後の一覧を返す。
 *
 * **1 件でも消せなければ何も消えない**（別の案件の明細 ID が混ざっていれば 404）。
 * DELETE ではなく POST なのは、消す対象を本文で渡すため。
 */
export function deleteProjectItems(
  projectId: number,
  itemIds: number[],
): Promise<ProjectItemListResponse> {
  return api.post<ProjectItemListResponse>(`/projects/${projectId}/items/delete`, {
    project_item_ids: itemIds,
  });
}

/** 見積を受注にして案件を起票するときに送る項目。 */
export interface OrderInput {
  project_title?: string | null;
  project_number?: string | null;
  client_po_number?: string | null;
  order_date?: string | null;
  deadline?: string | null;
  shipping_planned_date?: string | null;
  note?: string | null;
}

/**
 * 見積を受注にして案件を起票する。
 *
 * 得意先・営業担当は見積から引き継ぎ、明細は図面行だけがコピーされて
 * `quotation_item_id` が張られる。見積の状態は「受注」になる。
 */
export function createOrderFromQuotation(
  quotationId: number,
  input: OrderInput,
): Promise<Project> {
  return api.post<Project>(`/quotations/${quotationId}/order`, input);
}

// ---------------------------------------------------------------- 図面タブ
// 形は見積の図面タブと揃えてある（画面の部品を共有するため）。違うのは明細を
// 指す列名（`project_item_id`）と、出どころを示す列（`quotation_number`）だけ。

/** 案件の図面（API の ProjectDrawingResponse に対応）。 */
export interface ProjectDrawing {
  drawing_id: number;
  /** この図面から読み取った図番。まだ読み取っていなければ null。 */
  drawing_number: string | null;
  note: string | null;
  revision_count: number;
  latest_revision_id: number | null;
  /** 最新の版のサムネイルを取りに来る **API のパス**（`thumbnailSrc()` で使う）。 */
  thumbnail_path: string | null;
  thumbnail_status: ThumbnailStatus | null;
  ocr_status: OcrStatus | null;
  ocr_drawing_number: string | null;
  ocr_confidence: OcrConfidence | null;
  ocr_margin: number | null;
  /** 結び付いた案件明細。null なら未照合。 */
  project_item_id: number | null;
  item_sequence: number | null;
  item_name: string | null;
  /**
   * 出どころの見積番号。受注で見積から引き継いだ図面に入る。
   *
   * **入っている図面は案件側からは紐付けを変えることも削除することもできない**
   * （API が 409）。実体は見積と同じ 1 枚で、追従は見積 → 案件の一方向のため。
   */
  quotation_number: string | null;
  /**
   * 最新の版が QR 押印済みか。
   *
   * **版表記では QR 版と分からない**（客先の版表記と同じ採番を使う）ので、
   * バッジを出すかどうかはこれで決める。
   */
  qr_stamped: boolean;
  /**
   * まとめて押した QR の進み具合。`pending`=ワーカー待ち / `failed`=押せなかった。
   * **やり終えた依頼は消える**ので、済んだ図面は null に戻る。
   */
  qr_queue_status: "pending" | "failed" | null;
  created_at: string | null;
}

export interface ProjectDrawingListResponse {
  items: ProjectDrawing[];
  /** 明細に結び付いていない図面の数。 */
  unmatched_count: number;
}

export interface ProjectDrawingImportResponse extends ProjectDrawingListResponse {
  /** 作られた図面の数（＝ PDF のページ数）。 */
  created_count: number;
}

/** 選んだ図面から明細を起こした結果。 */
export interface ProjectDrawingBulkItemResponse extends ProjectDrawingListResponse {
  created_count: number;
  /** 図番が空で飛ばした図面の数。 */
  skipped_no_number_count: number;
  /** 既に紐付いていて飛ばした図面の数。 */
  skipped_linked_count: number;
}

export interface ProjectDrawingMatchResult {
  drawing_id: number;
  drawing_number: string | null;
  outcome: DrawingMatchOutcome;
  project_item_id: number | null;
  /** ambiguous のときの候補。 */
  candidate_item_ids: number[] | null;
}

/** QR 押印の結果（API の ProjectDrawingQrResponse に対応）。 */
export interface ProjectDrawingQrResponse {
  drawing_revision_id: number;
  revision: string;
  stamped_pages: number;
  /** 空白が取れず既存の線に重ねたページ数。0 でなければ警告する。 */
  fallback_pages: number;
  qr_payload: string | null;
}

/**
 * 図面に QR を押印する（**版が 1 つ増える**）。
 *
 * 押印の元は常に QR の付いていない最新の版なので、二重には押されない。押し直すと
 * 版は増えずに PDF が差し替わる。明細に紐付いていない図面は API が断る（400）。
 *
 * `area` は空白を探す区画（ページの 4 分割）。`auto` は紙全体から探す。
 */
export function stampProjectDrawingQr(
  projectId: number,
  drawingId: number,
  area: QrStampArea = "auto",
  /** 刷る大きさ（％）。押印欄だけはこの倍率の外で、いつでも実寸で刷られる。 */
  scalePercent = 100,
): Promise<ProjectDrawingQrResponse> {
  return api.post<ProjectDrawingQrResponse>(
    `/projects/${projectId}/drawings/${drawingId}/qr`,
    { area, scale_percent: scalePercent },
  );
}

/** まとめて依頼した結果（API の ProjectDrawingQrQueueResponse に対応）。 */
export interface ProjectDrawingQrQueueResponse extends ProjectDrawingListResponse {
  queued_count: number;
  skipped_unlinked_count: number;
  skipped_no_pdf_count: number;
  /** いまの版の QR が既にあるので飛ばした件数。 */
  skipped_already_count: number;
}

/**
 * 選んだ図面の QR 押印を**依頼する**（ここでは押さない）。
 *
 * `area` は**選んだ図面すべてに同じ区画**を使う（依頼の行に控えられ、ワーカーが
 * 押すときに使う）。
 *
 * 実際の押印は別プロセスのワーカーが 1 件ずつ行う。押せない図面（明細未紐付け・
 * PDF 無し・押印済み）は飛ばして数えて返る。
 */
export function stampProjectDrawingsQr(
  projectId: number,
  drawingIds: number[],
  area: QrStampArea = "auto",
  /** 刷る大きさ（％）。**選んだ図面すべてに同じ値**を使う。 */
  scalePercent = 100,
): Promise<ProjectDrawingQrQueueResponse> {
  return api.post<ProjectDrawingQrQueueResponse>(`/projects/${projectId}/drawings/qr`, {
    drawing_ids: drawingIds,
    area,
    scale_percent: scalePercent,
  });
}

export function listProjectDrawings(
  projectId: number,
): Promise<ProjectDrawingListResponse> {
  return api.get<ProjectDrawingListResponse>(`/projects/${projectId}/drawings`);
}

/**
 * PDF を 1 ファイル取り込む（ページごとに分かれて複数の図面になる）。
 *
 * ファイル本体は presigned URL で S3 へ直接 PUT する。API を経由させると、
 * 大きな PDF のたびに API のメモリと帯域を使う。**WAF が multipart のアップロード
 * を弾く**件もあり、経路を増やさないほうがよい。
 */
export async function importProjectDrawings(
  projectId: number,
  file: File,
): Promise<ProjectDrawingImportResponse> {
  const issued = await api.post<{ upload_url: string; s3_key: string }>(
    `/projects/${projectId}/drawings/upload-url`,
    { content_type: PDF_CONTENT_TYPE },
  );

  const res = await fetch(issued.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": PDF_CONTENT_TYPE },
  });
  if (!res.ok) {
    throw new Error(
      `PDF のアップロードに失敗しました（${res.status}）。もう一度お試しください。`,
    );
  }

  return api.post<ProjectDrawingImportResponse>(
    `/projects/${projectId}/drawings/import`,
    { s3_key: issued.s3_key, original_filename: file.name },
  );
}

/**
 * 図番で案件明細と突き合わせる。
 *
 * `dryRun` を立てると**何も保存せず**、何が起きるかだけ返る。判定はサーバーに
 * 任せる（規則を画面側に二重に持つと、見せた内容と実際の結果がずれる）。
 */
export function matchProjectDrawings(
  projectId: number,
  drawingIds?: number[],
  dryRun = false,
): Promise<{ results: ProjectDrawingMatchResult[] }> {
  return api.post<{ results: ProjectDrawingMatchResult[] }>(
    `/projects/${projectId}/drawings/match`,
    { drawing_ids: drawingIds ?? null, dry_run: dryRun },
  );
}

export function updateProjectDrawing(
  projectId: number,
  drawingId: number,
  input: { drawing_number?: string | null; note?: string | null },
): Promise<ProjectDrawingListResponse> {
  return api.patch<ProjectDrawingListResponse>(
    `/projects/${projectId}/drawings/${drawingId}`,
    input,
  );
}

/**
 * 図面を案件明細に結び付ける（null で外す）。
 *
 * **見積から引き継いだ図面は 409**。案件側で変えても、見積側で次に図面を触った
 * 時点で戻されるため。
 */
export function linkProjectDrawing(
  projectId: number,
  drawingId: number,
  projectItemId: number | null,
): Promise<ProjectDrawingListResponse> {
  return api.put<ProjectDrawingListResponse>(
    `/projects/${projectId}/drawings/${drawingId}/link`,
    { project_item_id: projectItemId },
  );
}

/** 図面の図番で案件明細を 1 行起こし、その図面を結び付ける。 */
export function createItemForProjectDrawing(
  projectId: number,
  drawingId: number,
): Promise<ProjectDrawingListResponse> {
  return api.post<ProjectDrawingListResponse>(
    `/projects/${projectId}/drawings/${drawingId}/item`,
    {},
  );
}

/** 選んだ図面それぞれの図番で案件明細を起こし、結び付ける。 */
export function createItemsForProjectDrawings(
  projectId: number,
  drawingIds: number[],
): Promise<ProjectDrawingBulkItemResponse> {
  return api.post<ProjectDrawingBulkItemResponse>(
    `/projects/${projectId}/drawings/items`,
    { drawing_ids: drawingIds },
  );
}

export function deleteProjectDrawing(
  projectId: number,
  drawingId: number,
): Promise<void> {
  return api.delete<void>(`/projects/${projectId}/drawings/${drawingId}`);
}

/** 選んだ図面をまとめて削除する（1 件でも消せなければ何も消えない）。 */
export function deleteProjectDrawings(
  projectId: number,
  drawingIds: number[],
): Promise<ProjectDrawingListResponse> {
  return api.post<ProjectDrawingListResponse>(
    `/projects/${projectId}/drawings/delete`,
    { drawing_ids: drawingIds },
  );
}

/** サムネイルがまだ出来ていない図面について、作り直しを仕掛ける。 */
export function rebuildProjectDrawingThumbnails(
  projectId: number,
  drawingIds?: number[],
): Promise<ProjectDrawingListResponse> {
  return api.post<ProjectDrawingListResponse>(
    `/projects/${projectId}/drawings/thumbnails/rebuild`,
    { drawing_ids: drawingIds ?? null },
  );
}

/** 図番をまだ読み取れていない図面について、読み直しを仕掛ける。 */
export function rereadProjectDrawingNumbers(
  projectId: number,
  drawingIds?: number[],
): Promise<ProjectDrawingListResponse> {
  return api.post<ProjectDrawingListResponse>(`/projects/${projectId}/drawings/ocr`, {
    drawing_ids: drawingIds ?? null,
  });
}

// ------------------------------------------------------------ 発注書の取り込み
// 読み取りは**ワーカーがやる**。Claude はインターネットに出る口で private
// サブネットの API からは届かないため、取り込みは待ち行列に入れるだけで即返る。
// 結果（明細）は案件詳細に後から出てくる。

/** 取り込んだ発注書の読み取り状態（API の PurchaseOrderStatus に対応）。 */
export type PurchaseOrderStatus = "PENDING" | "READY" | "NEEDS_REVIEW" | "FAILED";

/** 取り込んだ発注書 1 通（API の PurchaseOrderResponse に対応）。 */
export interface PurchaseOrder {
  purchase_order_id: number;
  project_id: number;
  original_filename: string | null;
  status: PurchaseOrderStatus;
  /** 読めなかった理由、または検算が合わなかった旨。画面に出す。 */
  error: string | null;
  /**
   * 取り込んだ結果の要約（「2 行を追加しました」など）。**上げ直したときに
   * 何が起きたか**を出すためのもの。読む前は null。
   */
  applied_summary: string | null;
  /** 以下は読み取れた伝票レベルの値。読む前（PENDING）は全部 null。 */
  po_number: string | null;
  order_date: string | null;
  deadline: string | null;
  quotation_number_raw: string | null;
  subtotal: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * 発注書 PDF を取り込む（図面と同じく presigned URL で S3 へ直接 PUT）。
 *
 * **返った時点ではまだ読んでいない**（`status` は `PENDING`）。
 */
export async function importPurchaseOrder(
  projectId: number,
  file: File,
): Promise<PurchaseOrder> {
  const issued = await api.post<{ upload_url: string; s3_key: string }>(
    `/projects/${projectId}/purchase-orders/upload-url`,
    { content_type: PDF_CONTENT_TYPE },
  );

  const res = await fetch(issued.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": PDF_CONTENT_TYPE },
  });
  if (!res.ok) {
    throw new Error(
      `発注書のアップロードに失敗しました（${res.status}）。もう一度お試しください。`,
    );
  }

  return api.post<PurchaseOrder>(`/projects/${projectId}/purchase-orders`, {
    s3_key: issued.s3_key,
    original_filename: file.name,
  });
}

export function listPurchaseOrders(
  projectId: number,
): Promise<{ items: PurchaseOrder[] }> {
  return api.get<{ items: PurchaseOrder[] }>(`/projects/${projectId}/purchase-orders`);
}

/**
 * 取り込んだ発注書の PDF を保存する。
 *
 * **期限付き URL ではなく API が実体を返す**（サムネイルと同じ方針）。期限付き
 * URL は持っている者が誰でも使える通行証で、開発者ツールやログに残るため。
 */
export async function downloadPurchaseOrder(
  projectId: number,
  purchaseOrderId: number,
): Promise<void> {
  const { blob, filename } = await api.download(
    `/projects/${projectId}/purchase-orders/${purchaseOrderId}/file`,
  );
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename ?? "発注書.pdf";
    anchor.click();
  } finally {
    // 解放しないと blob がタブを閉じるまで残る。
    URL.revokeObjectURL(url);
  }
}

/**
 * 案件から請求書 PDF を作って保存する（案件 1 件につき 1 通）。
 *
 * 発注書と同じく**期限付き URL ではなく API が実体を返す**。こちらは S3 にも
 * 置かず、呼ばれるたびに組み立てる（案件を直せば次に出した紙に反映される）。
 */
export async function downloadProjectInvoice(projectId: number): Promise<void> {
  const { blob, filename } = await api.download(`/projects/${projectId}/invoice`);
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename ?? "請求書.pdf";
    anchor.click();
  } finally {
    // 解放しないと blob がタブを閉じるまで残る。
    URL.revokeObjectURL(url);
  }
}
